import React from 'react';
import useToggle from 'hooks/useToggle';
import { LayoutCompanyContext } from 'components/mui/Layouts/Company/LayoutCompanyContext';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { DrawerHeader, DrawerToggle, DrawerToggleIcon } from 'components/mui/Layouts/components/Drawer';
import { LayoutRoot, LayoutContent } from 'components/mui/Layouts/components/Layout';
import { AppBarExpanded, AppBarItemDivider } from 'components/mui/Layouts/components/AppBar';
import LayoutCompanyDrawer from 'components/mui/Layouts/Company/components/Drawer';
import AppbarLogoCompany from 'components/mui/Layouts/components/AppbarLogoCompany';
import LocaleSelect from 'components/mui/LocaleSelect';
import AccountDropdown from 'components/mui/Layouts/components/AccountDropdown';
import EventsHistoryIconButton from 'components/mui/Layouts/components/EventsHistoryPanel/EventsHistoryIconButton';
import FullscreenIconButton from 'components/mui/Layouts/components/FullscreenIconButton';
import HelpDropdown from '../components/HelpDropdown';
import ApplicationDropdown from 'components/mui/Layouts/components/ApplicationDropdown';
import ChatLauncher from 'components/mui/Layouts/components/Chat';

type LayoutCompanyProps = {
  children: React.ReactNode;
  DrawerContent: React.ReactNode;
  suppressSearch?: boolean;
  defaultDrawerOpen?: boolean;
  // App-bar search slot. Routes inject their own (e.g. Workspace passes its consolidated
  // search); routes without search set `suppressSearch`.
  SearchComponent?: React.ReactNode;
};

const LayoutCompany: React.FC<LayoutCompanyProps> = ({
  children,
  DrawerContent,
  suppressSearch = false,
  defaultDrawerOpen = true,
  SearchComponent,
}) => {
  const [toggle, onToggle] = useToggle(defaultDrawerOpen);

  return (
    <LayoutRoot>
      <LayoutCompanyContext.Provider value={{ toggle, onToggle }}>
        <LayoutCompanyDrawer open={toggle}>{DrawerContent}</LayoutCompanyDrawer>
        <AppBarExpanded open={toggle}>
          <Box
            flexShrink={1}
            sx={{
              minWidth: 200,
              // Fixed cap, not a percentage: a percentage moves the truncation point with the
              // viewport, while the need is constant — the name plus the email/phone chips under
              // it. 400px fits both and leaves the rest of the bar to the search (DA-19687).
              maxWidth: 400,
            }}
          >
            <AppbarLogoCompany />
          </Box>
          {!suppressSearch && (
            <Box flexShrink={1} flexGrow={1} px={2} display="flex" justifyContent="center" minWidth={200}>
              {SearchComponent}
            </Box>
          )}

          <Stack direction="row" alignItems="center" spacing={1} flexShrink={0} ml="auto">
            <FullscreenIconButton />
            <AppBarItemDivider />
            <EventsHistoryIconButton />
            <ChatLauncher />
            <HelpDropdown />
            <LocaleSelect flagOnly />
            <ApplicationDropdown />
            <AccountDropdown />
          </Stack>
          {/* placed here because Drawer has overflow: hidden */}
          <DrawerToggle color="primary" size="small" onClick={onToggle}>
            <DrawerToggleIcon open={toggle} />
          </DrawerToggle>
        </AppBarExpanded>

        <LayoutContent>
          <DrawerHeader />
          {children}
        </LayoutContent>
      </LayoutCompanyContext.Provider>
    </LayoutRoot>
  );
};

export default LayoutCompany;
